import React, { useState } from 'react'
import { CloudCheck } from 'lucide-react'

import { Button } from '../../../global/components/ui/button.jsx'
import { Checkbox } from '../../../global/components/ui/checkbox.jsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../global/components/ui/dialog.jsx'
import { Label } from '../../../global/components/ui/label.jsx'
import { Separator } from '../../../global/components/ui/separator.jsx'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../global/components/ui/tooltip.jsx'
import { formatDate } from '../utils/documentUtils.js'

const SAVED_TOOLTIP =
  "All changes are automatically saved. You can leave this page and return later to upload more files. When you're completely finished, click 'I'm Finished, Notify Requesting Team' to send a final notification. Note: After clicking, you will no longer be able to access this request link."

const DocumentsHeader = ({
  selectedFirm,
  requestTask,
  documents,
  onFinishTask,
  unmatchedFiles = [],
  isSaving,
}) => {
  const isFinishButtonDisabled =
    !documents.flatMap(doc => doc.files || []).length && !unmatchedFiles.length

  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)

  const handleOpenModal = () => {
    setIsConfirmed(false)
    setOpenConfirmModal(true)
  }

  const handleCloseModal = () => {
    setOpenConfirmModal(false)
    setIsConfirmed(false)
  }

  const handleConfirmFinish = () => {
    onFinishTask()
    handleCloseModal()
  }

  return (
    <>
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div>
          <h3 className="tw-mb-1 tw-font-bold">
            {selectedFirm.name} is requesting files
          </h3>
          <p className="u-muted tw-mb-2 tw-text-sm">
            for {requestTask.description}
          </p>
          <p className="tw-mb-4 tw-text-sm">
            <strong>Requested:</strong> {formatDate(requestTask.requestDate)} |{' '}
            <strong>Due:</strong> {formatDate(requestTask.dueDate)}
          </p>
        </div>
        <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end">
          <Button
            disabled={isFinishButtonDisabled}
            onClick={handleOpenModal}
          >
            I&apos;m Finished, Notify Requesting Team
          </Button>
          {!isFinishButtonDisabled && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="tw-mt-2 tw-inline-flex tw-cursor-default tw-items-center tw-gap-2 tw-rounded-control tw-text-sm tw-font-bold focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring"
                >
                  <CloudCheck
                    aria-hidden="true"
                    className="tw-size-6"
                  />
                  {isSaving ? 'Saving...' : 'All changes saved'}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{SAVED_TOOLTIP}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <Separator />
      <Dialog
        open={openConfirmModal}
        onOpenChange={open => !open && handleCloseModal()}
      >
        <DialogContent className="sm:tw-max-w-xl">
          <DialogHeader>
            <DialogTitle>Confirm Submission</DialogTitle>
            <DialogDescription asChild>
              <div className="tw-flex tw-flex-col tw-gap-4 tw-pt-2 tw-text-sm">
                <p className="tw-mb-0">
                  I have completed uploading my files and the link to access
                  this portal will deactivate.
                </p>
                <p className="tw-mb-0">
                  My accountant will be notified that I have completed uploading
                  files.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="confirm-finish-request-task"
              checked={isConfirmed}
              onCheckedChange={checked => setIsConfirmed(checked === true)}
            />
            <Label htmlFor="confirm-finish-request-task">
              I understand and wish to proceed.
            </Label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              disabled={!isConfirmed}
              onClick={handleConfirmFinish}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DocumentsHeader
